// pages/AiOperations/AiOperations.jsx
//
// Super-admin "AI Operations" console. Shows every brand grouped by company
// with its LLM provider-mode (LLM API vs Claude session), lets you flip a
// brand/global between normal|auto|terminal, and launches an interactive
// Claude operator session (PTY over WebSocket → xterm canvas) that drains the
// terminal-provider queue. superAdmin only.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { startRegistration, startAuthentication } from "../../helpers/webauthn";
import { get, post, globalPut } from "../../api";

const MODE_COLOR = { normal: "#6b7280", auto: "#2563eb", terminal: "#059669", inherit: "#9ca3af" };
const BRAND_MODES = ["inherit", "normal", "auto", "terminal"];
const GLOBAL_MODES = ["normal", "auto", "terminal"];
const MODE_LABEL = { normal: "LLM API", auto: "LLM→Claude", terminal: "Claude session", inherit: "Inherit" };
const STAR_STR = { ONE: "★☆☆☆☆", TWO: "★★☆☆☆", THREE: "★★★☆☆", FOUR: "★★★★☆", FIVE: "★★★★★" };
const stars = (s) => STAR_STR[s] || (s ? String(s) : "");
const fmtWhen = (d) => { try { return new Date(d).toLocaleString(); } catch (_) { return ""; } };

export default function AiOperations() {
  const userInfo = useSelector((s) => s.user.userInfo);
  const isSuper = !!(userInfo && userInfo.superAdmin);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessionOn, setSessionOn] = useState(false);
  const [registered, setRegistered] = useState(null); // null=loading, bool after
  const [keyBusy, setKeyBusy] = useState(false);

  const termElRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);

  const fetchOverview = useCallback(async () => {
    try { const r = await get("/api/admin/ai-ops/overview"); setData(r); setErr(null); }
    catch (e) { setErr((e && e.message) || "Failed to load"); }
  }, []);

  const fetchKeyStatus = useCallback(async () => {
    try { const r = await get("/api/admin/ai-ops/webauthn/status"); setRegistered(!!(r && r.registered)); }
    catch (e) { setRegistered(false); }
  }, []);

  useEffect(() => { if (isSuper) { fetchOverview(); fetchKeyStatus(); } }, [fetchOverview, fetchKeyStatus, isSuper]);
  useEffect(() => () => teardownSession(), []); // cleanup on unmount

  if (!isSuper) return <Redirect to="/dashboard" />;

  const registerKey = async () => {
    setErr(null); setKeyBusy(true);
    try {
      const o = await post("/api/admin/ai-ops/webauthn/register/options", {}, false);
      if (!o || !o.options) throw new Error((o && o.error) || "no options");
      const attResp = await startRegistration(o.options);
      const v = await post("/api/admin/ai-ops/webauthn/register/verify", { attResp, label: "YubiKey" }, false);
      if (v && v.error) throw new Error(v.error);
      await fetchKeyStatus();
    } catch (e) { setErr("Key registration failed: " + ((e && e.message) || e)); }
    finally { setKeyBusy(false); }
  };

  const setBrandMode = async (brandId, brandName, providerMode) => {
    setBusy(true);
    try { await globalPut("/api/admin/ai-ops/brand-mode", { brandId, brandName, providerMode }, false); await fetchOverview(); }
    catch (e) { setErr((e && e.message) || "Toggle failed"); }
    finally { setBusy(false); }
  };
  const setBrandChatMode = async (brandId, brandName, chatMode) => {
    setBusy(true);
    try { await globalPut("/api/admin/ai-ops/brand-chat-mode", { brandId, brandName, chatMode }, false); await fetchOverview(); }
    catch (e) { setErr((e && e.message) || "Chat toggle failed"); }
    finally { setBusy(false); }
  };
  const setBrandReview = async (brandId, enabled) => {
    setBusy(true);
    try { await globalPut("/api/admin/ai-ops/brand-autopilot", { brandId, feature: "review", enabled }, false); await fetchOverview(); }
    catch (e) { setErr((e && e.message) || "Review toggle failed"); }
    finally { setBusy(false); }
  };
  const setBrandCommunity = async (brandId, enabled) => {
    setBusy(true);
    try { await globalPut("/api/admin/ai-ops/brand-autopilot", { brandId, feature: "community", enabled }, false); await fetchOverview(); }
    catch (e) { setErr((e && e.message) || "Community toggle failed"); }
    finally { setBusy(false); }
  };
  const setGlobal = async (providerMode) => {
    setBusy(true);
    try { await post("/api/admin/ai-ops/global-mode", { providerMode }, false); await fetchOverview(); }
    catch (e) { setErr((e && e.message) || "Global toggle failed"); }
    finally { setBusy(false); }
  };

  function teardownSession() {
    try { if (wsRef.current) wsRef.current.close(); } catch (e) {}
    try { if (termRef.current) termRef.current.dispose(); } catch (e) {}
    wsRef.current = null; termRef.current = null; fitRef.current = null;
  }

  const launchSession = async () => {
    setErr(null);
    // PRIMARY gate: FIDO2 assertion (touch the YubiKey) → mints the ws token.
    let token;
    try {
      const o = await post("/api/admin/ai-ops/webauthn/auth/options", {}, false);
      if (!o || !o.options) throw new Error((o && o.error) || "no security key registered");
      const asResp = await startAuthentication(o.options);
      const v = await post("/api/admin/ai-ops/webauthn/auth/verify", { asResp }, false);
      if (!v || !v.token) throw new Error((v && v.error) || "assertion failed");
      token = v.token;
    } catch (e) { return setErr("Security key check failed: " + ((e && e.message) || e)); }

    setSessionOn(true);
    // Defer so the terminal container is mounted.
    setTimeout(() => {
      const term = new Terminal({ cursorBlink: true, fontSize: 13, theme: { background: "#0b1020" }, convertEol: true });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(termElRef.current);
      try { fit.fit(); } catch (e) {}
      termRef.current = term; fitRef.current = fit;

      const cols = term.cols || 100, rows = term.rows || 30;
      const wsUrl = `${window.location.origin.replace(/^http/, "ws")}/ws/operator?token=${encodeURIComponent(token)}&cols=${cols}&rows=${rows}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => term.writeln("\x1b[36m… connecting to Claude operator session …\x1b[0m");
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch (e) { return; }
        if (m.type === "output") term.write(m.data);
        else if (m.type === "ready") term.writeln(`\x1b[32m● session ${m.sessionId} (pid ${m.pid}) ready\x1b[0m`);
        else if (m.type === "exit") { term.writeln(`\r\n\x1b[33m● session ended (code ${m.code})\x1b[0m`); }
        else if (m.type === "error") { term.writeln(`\r\n\x1b[31m● ${m.data}\x1b[0m`); }
      };
      ws.onclose = () => term.writeln("\r\n\x1b[33m● disconnected\x1b[0m");
      ws.onerror = () => term.writeln("\r\n\x1b[31m● websocket error\x1b[0m");

      term.onData((d) => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: "input", data: d })); });
      const onResize = () => { try { fit.fit(); if (ws.readyState === 1) ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows })); } catch (e) {} };
      window.addEventListener("resize", onResize);
    }, 60);
  };

  const startDraining = () => {
    if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify({ type: "starter", baseUrl: window.location.origin }));
  };
  const endSession = () => { teardownSession(); setSessionOn(false); fetchOverview(); };

  return (
    <div className="container py-4">
      <style>{`
        .aio-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
        .aio-co{border:1px solid #e5e7eb;border-radius:10px;margin-bottom:12px;background:#fff}
        .aio-co h5{margin:0;padding:10px 14px;border-bottom:1px solid #f0f0f0;display:flex;justify-content:space-between}
        .aio-brand{display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-bottom:1px solid #fafafa}
        .aio-pill{color:#fff;border-radius:999px;padding:1px 9px;font-size:11px;font-weight:600;margin-right:8px}
        .aio-btn{border:1px solid #d1d5db;background:#fff;border-radius:6px;padding:3px 9px;font-size:12px;cursor:pointer;margin-left:4px}
        .aio-btn.on{background:#111827;color:#fff;border-color:#111827}
        .aio-term{height:460px;background:#0b1020;border-radius:10px;padding:8px;margin-top:10px}
        .aio-warn{font-size:12px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:8px 10px;margin-bottom:12px}
      `}</style>

      <div className="aio-head">
        <div>
          <h3 style={{ margin: 0 }}>AI Operations</h3>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            Per-brand LLM routing + live Claude operator session.
            {data && data.reviewRepliesTotal > 0 && (
              <span style={{ marginLeft: 8 }}>· ⭐ {data.reviewRepliesTotal} Google review{data.reviewRepliesTotal === 1 ? "" : "s"} auto-replied</span>
            )}
          </div>
        </div>
        <div>
          <span style={{ fontSize: 12, color: "#6b7280", marginRight: 8 }}>Global:</span>
          {GLOBAL_MODES.map((m) => (
            <button key={m} className={`aio-btn ${data && data.globalMode === m ? "on" : ""}`} disabled={busy} onClick={() => setGlobal(m)}>{MODE_LABEL[m]}</button>
          ))}
        </div>
      </div>

      <div className="aio-warn">⚠ A launched session is an interactive Claude shell on the production server (runs as root). Gated by your security key (FIDO2) — touch it to start. Treat it like SSH access.</div>
      {err && <div className="alert alert-warning">{err}</div>}

      <div style={{ marginBottom: 8, fontSize: 13 }}>
        Security key:{" "}
        {registered === null ? <span style={{ color: "#9ca3af" }}>checking…</span>
          : registered ? <span style={{ color: "#059669", fontWeight: 600 }}>● registered (FIDO2 required to launch)</span>
          : <span style={{ color: "#d97706", fontWeight: 600 }}>● none registered</span>}
        {registered === false && <button className="aio-btn" style={{ marginLeft: 10 }} disabled={keyBusy} onClick={registerKey}>{keyBusy ? "touch your key…" : "Register YubiKey"}</button>}
      </div>

      <div style={{ marginBottom: 16 }}>
        {!sessionOn
          ? <button className="aio-btn on" style={{ fontSize: 14, padding: "7px 16px", opacity: registered ? 1 : 0.5 }} disabled={!registered} onClick={launchSession}>▶ Launch Claude operator session (touch key)</button>
          : (<>
              <button className="aio-btn" style={{ fontSize: 14, padding: "7px 16px" }} onClick={startDraining}>Start draining queue</button>
              <button className="aio-btn" style={{ fontSize: 14, padding: "7px 16px" }} onClick={endSession}>■ End session</button>
            </>)}
        {sessionOn && <div className="aio-term"><div ref={termElRef} style={{ height: "100%" }} /></div>}
      </div>

      {data && data.companies && data.companies.map((co) => (
        <div className="aio-co" key={co.companyId}>
          <h5>
            <span>{co.companyName}</span>
            <span style={{ fontSize: 12, color: co.pendingTasks ? "#d97706" : "#9ca3af", fontWeight: 400 }}>{co.pendingTasks} parked task{co.pendingTasks === 1 ? "" : "s"}</span>
          </h5>
          {co.brands.map((b) => (
            <div className="aio-brand" key={b.brandId}>
              <div>
                <span className="aio-pill" style={{ background: MODE_COLOR[b.effectiveMode] || "#6b7280" }}>{b.modeLabel}</span>
                <strong style={{ fontSize: 14 }}>{b.name}</strong>
                {b.override && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>(override: {b.override})</span>}
                <span className="aio-pill" style={{ background: MODE_COLOR[b.effectiveChatMode] || "#6b7280", marginLeft: 8, opacity: 0.85 }} title="Live chat routing">💬 {b.chatModeLabel}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#9ca3af", marginRight: 4 }}>Content</span>
                  {BRAND_MODES.map((m) => (
                    <button key={m} className={`aio-btn ${(b.override || "inherit") === m ? "on" : ""}`} disabled={busy} onClick={() => setBrandMode(b.brandId, b.name, m)}>{MODE_LABEL[m]}</button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#9ca3af", marginRight: 4 }}>Chat</span>
                  {BRAND_MODES.map((m) => (
                    <button key={m} className={`aio-btn ${(b.chatMode || "inherit") === m ? "on" : ""}`} disabled={busy} onClick={() => setBrandChatMode(b.brandId, b.name, m)}>{MODE_LABEL[m]}</button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center" }} title="Auto-reply to Google Business reviews with on-brand AI">
                  <span style={{ fontSize: 11, color: "#9ca3af", marginRight: 4 }}>Reviews</span>
                  <button className={`aio-btn ${b.reviewAutopilot ? "on" : ""}`} disabled={busy} onClick={() => setBrandReview(b.brandId, true)}>Auto-reply</button>
                  <button className={`aio-btn ${!b.reviewAutopilot ? "on" : ""}`} disabled={busy} onClick={() => setBrandReview(b.brandId, false)}>Off</button>
                  {b.reviewReplyCount > 0 && (
                    <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }} title="Reviews auto-replied (total · last 30 days)">
                      ⭐ {b.reviewReplyCount} replied{b.reviewReplyCount30d ? ` · ${b.reviewReplyCount30d}/30d` : ""}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center" }} title="Auto-community: weekly crawl the internet for fit communities, subscribe + reply on-brand">
                  <span style={{ fontSize: 11, color: "#9ca3af", marginRight: 4 }}>Community</span>
                  <button className={`aio-btn ${b.communityAutopilot ? "on" : ""}`} disabled={busy} onClick={() => setBrandCommunity(b.brandId, true)}>Auto</button>
                  <button className={`aio-btn ${!b.communityAutopilot ? "on" : ""}`} disabled={busy} onClick={() => setBrandCommunity(b.brandId, false)}>Off</button>
                </div>
              </div>
            </div>
          ))}
          {co.brands.length === 0 && <div style={{ padding: "8px 14px", color: "#9ca3af", fontSize: 13 }}>No brands</div>}
        </div>
      ))}

      {data && data.recentReviewReplies && data.recentReviewReplies.length > 0 && (
        <div className="aio-co">
          <h5>
            <span>Recent Google review replies</span>
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>spot-check the tone</span>
          </h5>
          {data.recentReviewReplies.map((r, i) => (
            <div key={i} style={{ padding: "8px 14px", borderTop: "1px solid #f1f1f1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#6b7280" }}>
                <span><span style={{ color: "#f59e0b" }}>{stars(r.starRating)}</span>{r.brandName ? <> · <strong>{r.brandName}</strong></> : null}</span>
                <span>{fmtWhen(r.repliedAt)}</span>
              </div>
              {r.reviewComment && <div style={{ fontSize: 13, color: "#525252", marginTop: 4 }}>“{r.reviewComment}”</div>}
              <div style={{ fontSize: 13, color: "#111827", marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #3a5765" }}>↳ {r.replyText}</div>
            </div>
          ))}
        </div>
      )}
      {data && (!data.companies || data.companies.length === 0) && <div style={{ color: "#6b7280" }}>No brands found.</div>}
    </div>
  );
}

AiOperations._test = { MODE_LABEL, BRAND_MODES, GLOBAL_MODES };
